import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

export interface BoardWheelNavigation {
  readonly enabled: boolean;
  readonly blocked: boolean;
  readonly canPrevious: boolean;
  readonly canNext: boolean;
  readonly context: string;
  readonly onStep: (direction: -1 | 1) => void;
}

/** Bind only to the board, never the page or a scrollable tool panel. */
export function bindBoardWheelNavigation(
  board: HTMLElement,
  getNavigation: () => BoardWheelNavigation,
  now: () => number = () => performance.now(),
) {
  let accumulated = 0;
  let lastEventAt = -Infinity;
  let lastStepAt = -Infinity;
  let direction = 0;
  let context: string | null = null;
  const reset = () => {
    accumulated = 0;
    lastEventAt = -Infinity;
    lastStepAt = -Infinity;
    direction = 0;
  };
  const wheel = (event: WheelEvent) => {
    const navigation = getNavigation();
    if (
      !navigation.enabled ||
      event.defaultPrevented ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.shiftKey ||
      !Number.isFinite(event.deltaY) ||
      event.deltaY === 0 ||
      Math.abs(event.deltaX) > Math.abs(event.deltaY)
    ) {
      reset();
      return;
    }
    // A passive React wheel listener cannot stop page scrolling on desktop browsers.
    // Keep the board stable even at the first/last move or during a rule request.
    event.preventDefault();
    if (navigation.blocked) {
      reset();
      return;
    }
    const time = now();
    const nextDirection = event.deltaY < 0 ? -1 : 1;
    if (context !== navigation.context || direction !== nextDirection || time - lastEventAt > 180) {
      reset();
    }
    context = navigation.context;
    direction = nextDirection;
    lastEventAt = time;
    if (nextDirection === -1 ? !navigation.canPrevious : !navigation.canNext) {
      accumulated = 0;
      return;
    }
    // Ignore trackpad noise; line/page wheels always count as one deliberate step.
    accumulated += event.deltaMode === 0 ? Math.abs(event.deltaY) : 24;
    if (time - lastStepAt < 80) {
      accumulated = 0;
      return;
    }
    if (accumulated < 24) return;
    accumulated = 0;
    lastStepAt = time;
    navigation.onStep(nextDirection);
  };
  board.addEventListener('wheel', wheel, { passive: false });
  return () => board.removeEventListener('wheel', wheel);
}

export function useBoardWheelNavigation(
  stageRef: RefObject<HTMLDivElement | null>,
  navigation: BoardWheelNavigation,
) {
  const latest = useRef(navigation);
  useLayoutEffect(() => {
    latest.current = navigation;
  });
  useEffect(() => {
    if (!navigation.enabled) return;
    const board = stageRef.current?.querySelector<HTMLElement>('.board-shell');
    if (!board) return;
    return bindBoardWheelNavigation(board, () => latest.current);
  }, [navigation.enabled, stageRef]);
}
